<script setup lang="ts">
import type { EventPricingBreakdownResponse } from '@@/types/api';
import { formatCurrency } from '~/composables/format-currency';

const props = defineProps<{
  breakdown: EventPricingBreakdownResponse | null;
  partySize: number;
  isInitialLoading: boolean;
  isRefetching: boolean;
  errorMessage?: string | null;
}>();

const showInitialSkeleton = computed(
  () => props.isInitialLoading && props.breakdown === null,
);
</script>

<template>
  <div class="pricing-breakdown mb-4">
    <v-alert
      v-if="errorMessage"
      density="compact"
      variant="tonal"
      type="error"
      class="mb-2"
    >
      {{ errorMessage }}
    </v-alert>

    <template v-if="showInitialSkeleton">
      <div class="pricing-breakdown__skeleton">
        <v-skeleton-loader type="text" class="mb-1" />
        <v-skeleton-loader type="text" class="mb-1" />
        <v-skeleton-loader type="text" class="mb-1" />
        <v-divider class="my-2" />
        <v-skeleton-loader
          type="heading"
          class="pricing-breakdown__total-skeleton"
        />
      </div>
    </template>

    <template v-else-if="breakdown">
      <div :class="{ 'pricing-breakdown--stale': isRefetching }">
        <div
          v-if="breakdown.accommodation_price > 0"
          class="d-flex justify-space-between text-body-2"
        >
          <span>Site fee</span>
          <span>{{ formatCurrency(breakdown.accommodation_price) }}</span>
        </div>
        <div class="d-flex justify-space-between text-body-2">
          <span>
            {{
              breakdown.accommodation_price > 0
                ? 'Per-person fee'
                : 'Ticket price'
            }}
            <template v-if="partySize > 1">
              ({{ formatCurrency(breakdown.ticket_price_per_person) }} &times;
              {{ partySize }})
            </template>
          </span>
          <span>{{ formatCurrency(breakdown.ticket_subtotal) }}</span>
        </div>
        <EventsServiceFeeLine
          v-if="breakdown.service_fee > 0"
          :amount="breakdown.service_fee"
        />
        <v-divider class="my-2" />
        <div
          class="d-flex justify-space-between align-center text-body-1 font-weight-bold"
        >
          <span class="d-inline-flex align-center ga-2">
            Total
            <v-progress-circular
              v-if="isRefetching"
              size="16"
              width="2"
              indeterminate
              aria-label="Recalculating total"
            />
          </span>
          <span>{{ formatCurrency(breakdown.total) }}</span>
        </div>
      </div>
    </template>
  </div>
</template>

<style scoped>
.pricing-breakdown--stale {
  opacity: 0.6;
  transition: opacity 120ms;
}
.pricing-breakdown__total-skeleton {
  max-width: 50%;
  margin-left: auto;
}
</style>
